import { ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/auth'
import { useLanguage } from '@eshop/shared'

interface BackofficeLayoutProps {
  children: ReactNode
}

interface NavItem {
  key: string
  path: string
  icon: string
}

const navItems: NavItem[] = [
  { key: 'dashboard', path: '/dashboard', icon: '📊' },
  { key: 'products', path: '/products', icon: '📦' },
  { key: 'orders', path: '/orders', icon: '🛒' },
]

export function BackofficeLayout({ children }: BackofficeLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { language, setLanguage, t } = useLanguage()
  const [sidebarOpen, setSidebarOpen] = useState(true)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-neutral-100">
      {/* Sidebar - Nolavi Design System */}
      <aside
        className={`${
          sidebarOpen ? 'w-72' : 'w-20'
        } bg-gradient-to-b from-blue-900 to-blue-800 text-white transition-all duration-300 flex flex-col shadow-xl`}
      >
        {/* Logo/Brand */}
        <div className="px-6 py-5 border-b border-blue-700/50">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-2xl font-bold tracking-tight">Nolavi</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-blue-700/50 transition-colors"
            >
              {sidebarOpen ? '◀' : '▶'}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all relative ${
                      isActive
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
                    }`}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full" />
                    )}
                    <span className="text-xl">{item.icon}</span>
                    {sidebarOpen && <span className="font-medium">{t(item.key as any)}</span>}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* User section */}
        <div className="px-4 py-5 border-t border-blue-700/50">
          <div className="flex items-center gap-4 px-2">
            {user?.avatar && (
              <img
                src={user.avatar}
                alt="Avatar"
                className="w-11 h-11 rounded-full ring-2 ring-blue-400/30"
              />
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-blue-200 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <button
              onClick={handleLogout}
              className="mt-4 w-full px-4 py-2.5 text-sm bg-blue-700/50 hover:bg-blue-600 text-white rounded-xl transition-colors font-medium"
            >
              {t('logout')}
            </button>
          )}
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-neutral-200">
          <div className="px-8 py-5">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-neutral-800">
                {t(navItems.find((item) => item.path === location.pathname)?.key as any) || 'Seller Portal'}
              </h2>
              <div className="flex items-center gap-6">
                {/* Language Selector */}
                <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-1">
                  <button
                    onClick={() => setLanguage('es')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      language === 'es'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    ES
                  </button>
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      language === 'en'
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    EN
                  </button>
                </div>
                <span className="text-sm text-neutral-500 hidden sm:block">
                  {new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-neutral-50">
          <div className="px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
